import React, { useState, useEffect } from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Building2, Landmark, Tags } from "lucide-react";
import MasterToolbarButton from "../../components/masterData/MasterToolbarButton";
import { toast } from "react-toastify";
import { ResponsiveDataList } from "../../components/responsive";
import DeleteModel from "../../components/DeleteModal";
import ParishCategoryApi from "../../api/parishCategoryApi";
import Helpers from "../../components/Helpers";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthUser } from "../../hooks/useAuthUser";
import MasterPageShell from "../../components/masterData/MasterPageShell";
import MasterSectionCard from "../../components/masterData/MasterSectionCard";
import MasterSearchField from "../../components/masterData/MasterSearchField";
import MasterRowActions from "../../components/masterData/MasterRowActions";
import MasterFormActions from "../../components/masterData/MasterFormActions";
import MasterDetailBlock from "../../components/masterData/MasterDetailBlock";
import styles from "../../components/masterData/MasterData.module.scss";
import { trimIdentifierFields } from "../../utils/trimFormFields";
import { ParishCategoryCard } from "./Parishes/manage/cards/ParishCategoryCard";

const ParishCategories = () => {
  let navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<any>({ name: "" });
  const [oldValues, setOldValues] = useState<Record<string, any>>({});
  const [list, setList] = useState<any[]>([]);
  const [icon_id, setIcon_id] = useState<any>("");
  const [validated, setValidated] = useState(false);
  const [show, setShow] = useState(false);
  const [LoadData, setLoadData] = useState(false);
  const [cp, setCp] = useState(false);
  const [isError, setIsError] = useState(false);
  const [searchKey, setSearchKey] = useState(
    () => (location.state as any)?.searchKeyword || "",
  );

  const userData = useAuthUser();

  const parishApi = new ParishCategoryApi();

  const handleChanges = (e: any) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.currentTarget.checkValidity()) {
      const payload = trimIdentifierFields(data, ["name"]);
      if (icon_id === "") {
        parishApi.addCategory(payload).then((res: any) => {
          console.log("Add Category Response:", res);
          if (res && res.success) {
            getList();
            toast.success(res.msg);
            clearfield();
          } else {
            toast.error(res?.msg || "Something went wrong");
          }
        });
      } else {
        parishApi.updateCategory(payload, icon_id).then((res: any) => {
          if (res && res.success) {
            getList();
            toast.success(res.msg);
            clearfield();
          } else {
            toast.error(res?.msg || "Something went wrong");
          }
        });
      }
    } else {
      setValidated(true);
    }
  };

  const clearfield = () => {
    setData({ name: "" });
    setIcon_id("");
  };

  const handleShow = (item: any) => {
    setShow(true);
    setIcon_id(item);
    setData({ name: item.name });
  };

  const handleClose = () => setShow(false);

  const DeleteData = () => {
    parishApi.deleteCategory(data, icon_id.id).then((res: any) => {
      if (res.success) {
        getList();
        toast.success(res.msg);
        clearfield();
      } else {
        toast.error(res.msg);
      }
      setShow(false);
    });
  };

  const getList = () => {
    setLoadData(true);
    parishApi
      .getAllCategories()
      .then((res: any) => {
        console.log("API Response ===>", res); // ✅ Check output here
        const sorted = res.data.sort((a, b) => a.name.localeCompare(b.name));
        setList(sorted);
        setLoadData(false);
      })
      .catch((err: any) => {
        console.error("Error fetching categories:", err); // ⚠️ Show error in console
        setLoadData(false);
      });
  };

  const SearchData = (event: any) => {
    setLoadData(true);
    const key = event.target.value;
    setSearchKey(key);
    if (key) {
      console.warn(key);
      parishApi
        .searchCategory(key) // ✅ Fix here
        .then((response: any) => {
          setList(response.data);
          setLoadData(false);
        })
        .catch((error: any) => {
          setLoadData(false);
          toast.error(error.toString());
        });
    } else {
      setLoadData(false);
      getList();
    }
  };

  useEffect(() => {
    if (searchKey) {
      SearchData({ target: { value: searchKey } });
    } else {
      getList();
    }
  }, []);

  const isEditing = icon_id !== "";

  const columns = [
    ...(userData?.userRole === "ADMIN"
      ? [
          {
            id: 2,
            name: "Action",
            cell: (row) => (
              <MasterRowActions
                onEdit={() => {
                  setIcon_id(row.id);
                  setData({ name: row.name });
                }}
                onDelete={() => handleShow(row)}
                showDelete={row.name !== "OTHER"}
              />
            ),
          },
        ]
      : []),

    {
      id: 1,
      name: "Parish Category",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <OverlayTrigger
          placement="right"
          overlay={<Tooltip>{row.name}</Tooltip>}
        >
          <span>{row.name}</span>
        </OverlayTrigger>
      ),
    },

    {
      id: 3,
      name: "Created At",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip>
              {Helpers.convertUtcToTimezone(
                row.createdAt,
                "MM-DD-YYYY hh:mm A"
              )}
            </Tooltip>
          }
        >
          <span>
            {Helpers.convertUtcToTimezone(row.createdAt, "MM-DD-YYYY")}
          </span>
        </OverlayTrigger>
      ),
    },
  ];

  return (
    <MasterPageShell>
      <MasterSectionCard
        title={isEditing ? "Edit Parish Category" : "Add Parish Category"}
        subtitle="Category names group parishes for reports and filters."
        icon={<Tags size={20} color="#285d8f" />}
        bgcolor="#e8f3fc"
      >
        <Form onSubmit={handleSubmit} noValidate validated={validated}>
          <Form.Group>
            <Form.Label>Parish Category</Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Enter new category"
              name="name"
              value={data.name}
              onChange={handleChanges}
            />
            <Form.Control.Feedback type="invalid">
              Parish category is required!
            </Form.Control.Feedback>
          </Form.Group>
          <MasterFormActions
            isEditing={isEditing}
            onCancel={clearfield}
            disabled={cp || isError}
            showCancel={isEditing}
          />
        </Form>
      </MasterSectionCard>

      <MasterSectionCard
        title="Parish Categories"
        subtitle="Search and maintain the parish category list."
        icon={<Tags size={20} color="#285d8f" />}
        total={list?.length}
        bgcolor="#f3f7fb"
        toolbar={
          <>
            <MasterToolbarButton
              icon={<Landmark size={16} />}
              onClick={() => navigate("/deaneries")}
            >
              Manage Deaneries
            </MasterToolbarButton>
            <MasterToolbarButton
              icon={<Building2 size={16} />}
              onClick={() => navigate("/parish")}
            >
              Manage Parish
            </MasterToolbarButton>
            <MasterSearchField
              onChange={SearchData}
              value={searchKey}
              placeholder="Search..."
            />
          </>
        }
        bodyPadding={false}
      >
        <div className={styles.tableWrap}>
          <ResponsiveDataList
            loading={LoadData}
            data={list}
            columns={columns}
            pointerOnHover
            highlightOnHover
            isServerSide={false}
            pagination
            countPerPage={20}
            cardGridColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
            renderCard={(row) => (
              <ParishCategoryCard
                row={row}
                isAdmin={userData?.userRole === "ADMIN"}
                onEdit={(r) => {
                  setIcon_id(r.id);
                  setData({ name: r.name });
                }}
                onDelete={handleShow}
              />
            )}
          />
        </div>
      </MasterSectionCard>

      <DeleteModel
        show={show}
        content={
          <MasterDetailBlock
            title="Parish Category Detail"
            label="Parish Category"
            value={icon_id.name}
          />
        }
        handleClose={handleClose}
        DeleteData={DeleteData}
      />
    </MasterPageShell>
  );
};

export default ParishCategories;
